import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { RichTextEditor } from '../components/RichTextEditor';
import { sanitizeRichHtml } from '../utils/sanitizeHtml';
import {
  ThumbsUp,
  MessageSquare,
  Eye,
  Send,
  ArrowLeft,
  User,
  ChevronRight,
  ExternalLink,
  Trash2,
  Edit,
  Check,
  X,
  Calendar,
} from 'lucide-react';
import { newsroomApi, NewsroomLiker } from '../services/newsroomApi';
import { useAuthStore } from '../store/authStore';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

export const NewsroomArticlePage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [comment, setComment] = useState('');
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editDate, setEditDate] = useState('');

  const { data: article, isLoading } = useQuery({
    queryKey: ['newsroom-article', id],
    queryFn: () => newsroomApi.getArticle(id!),
    enabled: !!id,
  });

  const likeMutation = useMutation({
    mutationFn: () => newsroomApi.toggleLike(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsroom-article', id] });
    },
    onError: () => toast.error(t('newsroom.error')),
  });

  const commentMutation = useMutation({
    mutationFn: (content: string) => newsroomApi.addComment(id!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsroom-article', id] });
      setComment('');
      toast.success(t('newsroom.commentPosted'));
    },
    onError: () => toast.error(t('newsroom.error')),
  });

  const updateTitleMutation = useMutation({
    mutationFn: () => newsroomApi.updateArticle(id!, { title: editTitle }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsroom-article', id] });
      queryClient.invalidateQueries({ queryKey: ['newsroom-articles'] });
      setIsEditingTitle(false);
      toast.success(t('newsroom.titleUpdated'));
    },
    onError: () => toast.error(t('newsroom.error')),
  });

  const updateDateMutation = useMutation({
    mutationFn: () =>
      newsroomApi.updateArticle(id!, { published_at: new Date(editDate).toISOString() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsroom-article', id] });
      queryClient.invalidateQueries({ queryKey: ['newsroom-articles'] });
      setIsEditingDate(false);
      toast.success(t('newsroom.dateUpdated'));
    },
    onError: () => toast.error(t('newsroom.error')),
  });

  const deleteArticleMutation = useMutation({
    mutationFn: () => newsroomApi.deleteArticle(id!),
    onSuccess: () => {
      toast.success(t('newsroom.articleDeleted'));
      navigate('/newsroom');
    },
    onError: () => toast.error(t('newsroom.error')),
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => newsroomApi.deleteComment(id!, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsroom-article', id] });
      toast.success(t('newsroom.commentDeleted'));
    },
    onError: () => toast.error(t('newsroom.error')),
  });

  const { data: likers = [], isLoading: likersLoading } = useQuery<NewsroomLiker[]>({
    queryKey: ['newsroom-article-likes', id],
    queryFn: () => newsroomApi.getLikes(id!),
    enabled: showLikesModal && !!id,
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="aspect-video bg-gray-200 rounded-lg" />
          <div className="h-8 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-100 rounded w-full" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <p className="text-gray-500 text-center py-8">{t('newsroom.articleNotFound')}</p>
        </Card>
      </div>
    );
  }

  const canEdit = isAdmin || (user && article.author_id === user.id);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap">
        <Link to="/newsroom" className="flex items-center gap-1 hover:text-[#0B1F3A]">
          <ArrowLeft className="w-3.5 h-3.5" />
          {t('newsroom.title')}
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to={`/newsroom/kanaal/${article.channel_slug}`} className="hover:text-[#0B1F3A]">
          {article.channel_name}
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-gray-700 truncate max-w-[200px]">{article.title}</span>
      </nav>

      {article.cover_image_url && (
        <div className="aspect-video overflow-hidden rounded-xl mb-6 bg-gray-100">
          <img
            src={article.cover_image_url}
            alt={article.title}
            className="w-full h-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}

      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ backgroundColor: article.channel_color + '20', color: article.channel_color }}
          >
            {article.channel_name}
          </span>

          {isEditingDate ? (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                autoFocus
              />
              <button
                onClick={() => updateDateMutation.mutate()}
                disabled={updateDateMutation.isPending || !editDate}
                className="text-green-600 hover:text-green-700 disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setIsEditingDate(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <span className="flex items-center gap-1 text-xs text-gray-400 group">
              {format(new Date(article.published_at), 'd MMMM yyyy', { locale: nl })}
              {isAdmin && (
                <button
                  onClick={() => {
                    setEditDate(format(new Date(article.published_at), 'yyyy-MM-dd'));
                    setIsEditingDate(true);
                    setIsEditingTitle(false);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-[#0B1F3A] transition-opacity ml-1"
                  title={t('newsroom.editDate')}
                >
                  <Calendar className="w-3 h-3" />
                </button>
              )}
            </span>
          )}
        </div>

        {isEditingTitle ? (
          <div className="mb-4">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              maxLength={255}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xl font-bold focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] mb-2"
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => updateTitleMutation.mutate()}
                loading={updateTitleMutation.isPending}
                disabled={!editTitle.trim() || editTitle.trim().length < 3}
              >
                <Check className="w-3.5 h-3.5 mr-1" /> {t('newsroom.save')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditingTitle(false)}>
                <X className="w-3.5 h-3.5 mr-1" /> {t('newsroom.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <h1 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">{article.title}</h1>
        )}

        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
          <div className="w-10 h-10 rounded-full bg-[#0B1F3A] flex items-center justify-center text-white flex-shrink-0 overflow-hidden">
            {article.profile_photo_path ? (
              <img src={`/uploads/${article.profile_photo_path}`} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <User className="w-5 h-5" />
            )}
          </div>
          <div>
            <div className="font-medium text-gray-900">{article.first_name} {article.last_name}</div>
            <div className="text-xs text-gray-400">
              {t('newsroom.publishedOn', { date: format(new Date(article.published_at), 'd MMMM yyyy', { locale: nl }) })}
            </div>
          </div>
        </div>

        {article.excerpt && (
          <p className="text-gray-600 leading-relaxed mb-6">{article.excerpt}</p>
        )}

        {article.external_url && (
          <a
            href={article.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#0B1F3A] text-white rounded-lg font-medium hover:bg-[#1A3459] transition-colors mb-6"
          >
            <ExternalLink className="w-4 h-4" />
            {t('newsroom.readArticle')}
          </a>
        )}

        <div className="pt-4 border-t border-gray-100 flex items-center gap-4 text-sm text-gray-500 flex-wrap">
          <div className="flex items-center gap-1">
            <button
              onClick={() => likeMutation.mutate()}
              disabled={likeMutation.isPending}
              className={`flex items-center gap-1.5 transition-colors hover:text-[#0B1F3A] ${article.user_liked ? 'text-[#0B1F3A]' : ''}`}
              title={article.user_liked ? t('newsroom.likeRemove') : t('newsroom.likeAdd')}
            >
              <ThumbsUp className={`w-4 h-4 ${article.user_liked ? 'fill-current' : ''}`} />
            </button>
            {article.likes_count > 0 ? (
              <button
                onClick={() => setShowLikesModal(true)}
                className="hover:underline hover:text-[#0B1F3A] transition-colors"
              >
                {t('newsroom.like', { count: article.likes_count })}
              </button>
            ) : (
              <span>{t('newsroom.like', { count: article.likes_count })}</span>
            )}
          </div>
          <span className="flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4" />
            {t('newsroom.comment', { count: article.comments_count })}
          </span>
          <span className="flex items-center gap-1.5">
            <Eye className="w-4 h-4" />
            {t('newsroom.views', { count: article.views_count })}
          </span>

          {canEdit && !isEditingTitle && !isEditingDate && (
            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={() => {
                  setEditTitle(article.title);
                  setIsEditingTitle(true);
                  setIsEditingDate(false);
                }}
                className="flex items-center gap-1.5 text-gray-400 hover:text-[#0B1F3A] transition-colors"
              >
                <Edit className="w-4 h-4" />
                {t('newsroom.editTitle')}
              </button>
              <button
                onClick={() => {
                  if (window.confirm(t('newsroom.confirmDelete'))) {
                    deleteArticleMutation.mutate();
                  }
                }}
                className="flex items-center gap-1.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                {t('newsroom.deleteArticle')}
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* Comments */}
      {article.comments && article.comments.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {t('newsroom.comment', { count: article.comments.length })}
          </h2>
          <div className="space-y-3">
            {article.comments.map((c) => (
              <Card key={c.id} className="!p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {c.profile_photo_path ? (
                      <img src={`/uploads/${c.profile_photo_path}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <User className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-900">{c.first_name} {c.last_name}</span>
                      <span className="text-xs text-gray-400">
                        {format(new Date(c.created_at), 'd MMM yyyy HH:mm', { locale: nl })}
                      </span>
                      {(isAdmin || (user && c.author_id === user.id)) && (
                        <button
                          onClick={() => {
                            if (window.confirm(t('newsroom.confirmDeleteComment'))) {
                              deleteCommentMutation.mutate(c.id);
                            }
                          }}
                          className="ml-auto flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div
                      className="text-sm text-gray-700 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(c.content) }}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Likes modal */}
      {showLikesModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowLikesModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <ThumbsUp className="w-4 h-4 text-[#0B1F3A]" />
                {t('newsroom.like', { count: article.likes_count })}
              </h3>
              <button onClick={() => setShowLikesModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {likersLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 animate-pulse">
                    <div className="w-8 h-8 rounded-full bg-gray-200" />
                    <div className="h-3 bg-gray-200 rounded w-32" />
                  </div>
                ))}
              </div>
            ) : likers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">{t('newsroom.noLikers')}</p>
            ) : (
              <ul className="space-y-2 max-h-72 overflow-y-auto">
                {likers.map((liker) => (
                  <li key={liker.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {liker.profile_photo_path ? (
                        <img src={`/uploads/${liker.profile_photo_path}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-900">{liker.first_name} {liker.last_name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Add comment */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-3">{t('newsroom.writeComment')}</h3>
        <RichTextEditor
          content={comment}
          onChange={setComment}
          placeholder={t('newsroom.commentPlaceholder')}
          minHeight={100}
          simple
        />
        <div className="mt-3 flex justify-between items-center">
          <Link
            to={`/newsroom/kanaal/${article.channel_slug}`}
            className="text-sm text-[#0B1F3A] hover:underline flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('newsroom.backTo', { channel: article.channel_name })}
          </Link>
          <Button
            onClick={() => {
              const plain = comment.replace(/<[^>]+>/g, '').trim();
              if (plain.length < 2) {
                toast.error(t('newsroom.commentTooShort'));
                return;
              }
              commentMutation.mutate(comment);
            }}
            loading={commentMutation.isPending}
            disabled={!comment || comment === '<p></p>'}
          >
            <Send className="w-4 h-4 mr-2" />
            {t('newsroom.reply')}
          </Button>
        </div>
      </Card>
    </div>
  );
};
